import { useAuth, useSignIn, useUser } from '@clerk/clerk-react';
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors';
import { Box, TextField, Typography } from '@material-ui/core';
import { LoadSpin } from 'components/Loading/Spin';
import { CenterText } from 'components/styles';
import { useQuery as useQueryString } from 'hooks/useQuery';
import ROUTES from 'pages/routes-enum';
import { useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useGetUser } from 'service/user';
import { showErrorToast, showInfoToast } from 'utils/toast';
import splitcLogo from '../../logo.svg';
import { usePassword } from './Onboarding/hooks';
import * as S from './Onboarding/styles';
import { useUpdatePassword } from './hooks/useUpdatePassword';
import { Logo } from './styles';
import { buildState, validatePassword } from './utils';

type QueryParams = {
  provider: number;
};

const UsernameSignIn = () => {
  const history = useHistory();
  const { data: user } = useGetUser();
  const { sessionId, isSignedIn } = useAuth();
  const { signIn, isLoaded: isSignInLoaded } = useSignIn();
  const { user: clerkUser } = useUser();

  const { inputsData, renderInputAdornment, handleInputWithError, handleInputChange } =
    usePassword();
  const { mutateAsync: updatePassword, isLoading: isUpdatingPassword } = useUpdatePassword();

  const [username, setUsername] = useState(clerkUser?.username);
  const [signInError, setSignInError] = useState<string>();
  const [isSigningIn, setIsSignIn] = useState(false);

  const { provider } = useQueryString<QueryParams>();
  const firstLogin = Boolean(clerkUser?.publicMetadata.shouldChangePassword);
  const isLoading = !isSignInLoaded || isUpdatingPassword || isSigningIn;

  const handleUsernameSignIn = async () => {
    const password = inputsData.password.value;

    if (!isSignInLoaded || !username || !password || !provider) return;

    setIsSignIn(true);

    try {
      const { createdSessionId } = await signIn.create({ identifier: username, password });
      const state = buildState(provider);
      history.push(`/callback?__clerk_created_session=${createdSessionId}&state=${state}`);
    } catch (err) {
      if (!isClerkAPIResponseError(err)) {
        showErrorToast('Não foi possível realizar o login... Tente novamente');
        return;
      }

      const errorMessage =
        {
          form_identifier_not_found: 'Credencial inválida',
          form_password_incorrect: 'Credencial inválida',
          authorization_invalid: `Sua conta está suspensa. Entre em contato com o suporte para mais informações.`,
        }[err.errors[0].code] || 'Erro desconhecido';

      setSignInError(errorMessage);
    } finally {
      setIsSignIn(false);
    }
  };

  const handlePasswordUpdate = async () => {
    await updatePassword(
      {
        officeId: user?.escritorio.id_escritorio,
        sessionId: sessionId ?? undefined,
        password: inputsData.password.value,
      },
      {
        onSuccess: () => {
          showInfoToast('Senha alterada com sucesso');
          clerkUser?.reload().then(() => history.push(ROUTES.ROOT_HOME));
        },
        onError: () => {
          showErrorToast('Não foi possível alterar a senha... Tente novamente');
        },
      },
    );
  };

  const handleRollback = async () => {
    if (isSignedIn) {
      history.push(ROUTES.LOGOUT);
      return;
    }

    history.push(ROUTES.SIGNIN);
  };

  if (isSignInLoaded && isSignedIn && !clerkUser?.publicMetadata.shouldChangePassword)
    return <Redirect to={ROUTES.ROOT_HOME} />;

  return (
    <S.LoginContainer>
      <Box mb={5}>
        <CenterText>
          <Logo src={splitcLogo} alt="splitc-logo" data-testid="splitc-logo" />
          <Typography color="initial" variant="h4">
            SplitC
          </Typography>
        </CenterText>
      </Box>

      {isLoading ? (
        <LoadSpin height="500px" width="500px" />
      ) : (
        <>
          {firstLogin && (
            <Box mb={5}>
              <Typography color="primary" variant="subtitle1">
                Esse é seu primeiro login, para sua segurança faça a alteração da senha
              </Typography>
            </Box>
          )}

          <S.FormContainer>
            <TextField
              label="Usuário"
              variant="outlined"
              fullWidth
              value={clerkUser?.username}
              InputLabelProps={{
                shrink: !!username,
              }}
              onChange={({ target }) => setUsername(target.value?.toLowerCase())}
              disabled={firstLogin}
            />
            <TextField
              fullWidth
              label="Senha"
              type={inputsData.password.hide ? 'password' : 'text'}
              variant="outlined"
              onKeyDown={event => {
                if (!firstLogin && event.key === 'Enter') handleUsernameSignIn();
              }}
              value={inputsData.password.value}
              error={firstLogin && Boolean(inputsData.password.error)}
              onChange={({ target }) => {
                const { value } = target;
                if (!firstLogin) return handleInputChange('password', value);

                const validators = validatePassword(value);

                if (Object.values(validators).length > 0)
                  handleInputWithError('password', validators);
                else handleInputWithError('password', null);

                if (value !== inputsData.passwordConfirmation.value)
                  handleInputWithError('passwordConfirmation', 'Senhas não coincidem');
                else handleInputWithError('passwordConfirmation', '');

                handleInputChange('password', value);
              }}
              InputProps={{ endAdornment: renderInputAdornment('password') }}
            />

            {firstLogin && (
              <>
                <TextField
                  fullWidth
                  label="Confirmação de senha"
                  value={inputsData.passwordConfirmation.value}
                  variant="outlined"
                  type={inputsData.passwordConfirmation.hide ? 'password' : 'text'}
                  onKeyDown={event => {
                    if (firstLogin && event.key === 'Enter') handlePasswordUpdate();
                  }}
                  onChange={({ target }) => {
                    const { value } = target;

                    if (value !== inputsData.password.value)
                      handleInputWithError('passwordConfirmation', 'Senhas não coincidem');
                    else handleInputWithError('passwordConfirmation', '');

                    handleInputChange('passwordConfirmation', value);
                  }}
                  error={inputsData.password.value !== inputsData.passwordConfirmation.value}
                  helperText={
                    inputsData.password.value !== inputsData.passwordConfirmation.value
                      ? 'Senhas não correspondem'
                      : ''
                  }
                  InputProps={{ endAdornment: renderInputAdornment('passwordConfirmation') }}
                />

                {inputsData.password.error && (
                  <S.ValidatorsContainer>
                    <ul>
                      {Object.values(inputsData.password.error).map(error => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </S.ValidatorsContainer>
                )}
              </>
            )}

            {signInError && (
              <S.ValidatorsContainer>
                <ul>{signInError}</ul>
              </S.ValidatorsContainer>
            )}

            <S.Button
              color="primary"
              onClick={firstLogin ? handlePasswordUpdate : handleUsernameSignIn}
              fullWidth
              disabled={
                !username?.trim() ||
                !inputsData.password?.value.trim() ||
                Boolean(inputsData.password.error) ||
                (firstLogin && Boolean(inputsData.passwordConfirmation.error))
              }
            >
              {firstLogin ? 'Alterar senha' : 'Login'}
            </S.Button>

            <S.Button color="primary" onClick={handleRollback} fullWidth variant="text">
              Voltar para o login
            </S.Button>
          </S.FormContainer>
        </>
      )}
    </S.LoginContainer>
  );
};

export default UsernameSignIn;
